import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import LoginForm from '@/components/auth/LoginForm';
import { ArrowLeft, Zap, Users, TrendingUp, Newspaper } from 'lucide-react';

import { z } from 'zod';

// Define the login schema first
const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6)
});

// Then infer the type from the schema
type LoginFormValues = z.infer<typeof loginSchema>;

const Login = () => {
  const navigate = useNavigate();
  const login = useAuthStore(state => state.login);
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const isLoading = useAuthStore(state => state.isLoading);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  
  // Fade in animations on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Redirect if already authenticated (but wait for auth to finish loading)
  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      // Small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        if (user.role === 'doctor') {
          navigate('/doctor-dashboard', { replace: true });
        } else if (user.role === 'super_admin') {
          navigate('/super-admin', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [user, isAuthenticated, isLoading, navigate]);
  
  // Show loading while auth is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Don't render login form if already authenticated (will redirect via useEffect)
  if (isAuthenticated) {
    return null;
  }
  
  const onSubmit = async (data: LoginFormValues) => {
    setIsSubmitting(true);
    
    try {
      const success = await login({
        email: data.email,
        password: data.password
      });
      
      if (success) {
        toast({
          title: "Welcome",
          description: "Welcome to AM CARE",
        });
      } else {
        toast({
          title: "Login failed",
          description: "Invalid email or password",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Login error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex relative bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      {/* Left Side - Informative Content */}
      <div className="hidden lg:flex lg:w-1/2 relative px-12 py-12">
        {/* Logo - Top Left */}
        <div className="absolute top-12 left-12 flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 shadow-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg tracking-tight">AM</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">AM CARE</h1>
        </div>

        {/* Informative Content - Right Aligned */}
        <div className="w-full max-w-lg ml-auto mr-0 flex flex-col justify-center pr-8">
          <div className="space-y-6">
            <div 
              className={`flex items-start gap-3 transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
              style={{ transitionDelay: '0.1s' }}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center shadow-md transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1.5">Get started quickly</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Start managing patients and streamlining your practice in minutes</p>
              </div>
            </div>

            <div 
              className={`flex items-start gap-3 transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
              style={{ transitionDelay: '0.3s' }}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center shadow-md transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1.5">Delight patients & grow your business</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Keep patients coming back with easy queue management, e-prescriptions, and comprehensive care</p>
              </div>
            </div>

            <div 
              className={`flex items-start gap-3 transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
              style={{ transitionDelay: '0.5s' }}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center shadow-md transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1.5">Streamline operations</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Manage queues, prescriptions, and patient records all in one platform</p>
              </div>
            </div>

            <div 
              className={`flex items-start gap-3 transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
              style={{ transitionDelay: '0.7s' }}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center shadow-md transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <Newspaper className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1.5">Stay up-to-date on healthcare trends</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Join thousands of healthcare professionals who trust AM CARE for modern hospital management</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-start px-8 py-12 lg:pl-8">
        <div className="w-full max-w-md">
          {/* Back to Home Button - Mobile */}
          <div 
            className={`lg:hidden mb-6 transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
            style={{ transitionDelay: '0.2s' }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>
          </div>

          <Card 
            className={`shadow-xl border border-gray-200 rounded-2xl bg-white overflow-hidden transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
            }`}
            style={{ transitionDelay: '0.4s' }}
          >
            {/* Decorative Top Border */}
            <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
            
            <CardHeader className="pb-6 pt-8 px-8">
              {/* Back to Home Button - Desktop */}
              <div 
                className={`hidden lg:block mb-4 transition-all duration-700 ease-out ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                }`}
                style={{ transitionDelay: '0.5s' }}
              >
                <Link 
                  to="/" 
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors font-medium"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Home
                </Link>
              </div>
              
              {/* AM CARE Logo */}
              <div 
                className={`flex items-center justify-center gap-2 mb-6 transition-all duration-700 ease-out ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                }`}
                style={{ transitionDelay: '0.6s' }}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 shadow-lg flex items-center justify-center transition-transform duration-300 hover:scale-110 hover:rotate-3">
                  <span className="text-white font-bold text-lg">AM</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">AM CARE</h2>
              </div>
              
              <CardTitle 
                className={`text-gray-900 text-3xl font-bold text-center mb-2 transition-all duration-700 ease-out ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: '0.7s' }}
              >
                Welcome Back
              </CardTitle>
              <CardDescription 
                className={`text-gray-600 text-center text-base transition-all duration-700 ease-out ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: '0.8s' }}
              >
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent 
              className={`px-8 pb-6 transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '0.9s' }}
            >
              <LoginForm 
                defaultEmail=""
                defaultPassword=""
                onSubmit={onSubmit}
                isLoading={isSubmitting}
                userRole="User"
              />
            </CardContent>
            <CardFooter 
              className={`pt-4 pb-8 px-8 flex flex-col gap-3 transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '1s' }}
            >
              <div className="text-center w-full">
                <div className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <a 
                    href="/signup" 
                    className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/signup');
                    }}
                  >
                    Sign up for your hospital
                  </a>
                </div>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
